import { create } from 'zustand';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

type NewYearSeasonState = {
  isNewYearSeason: boolean;
  nextCheckAt: number | null;
  start: () => void;
  stop: () => void;
  refresh: () => void;
};

type SeasonStatus = {
  isNewYearSeason: boolean;
  nextBoundary: Date;
};

const getSeasonStatus = (now: Date = new Date()): SeasonStatus => {
  const year = now.getFullYear();
  const month = now.getMonth();
  let start: Date;
  let end: Date;

  if (month === 0) {
    start = new Date(year - 1, 11, 1, 0, 0, 0, 0);
    end = new Date(year, 1, 1, 0, 0, 0, 0);
  } else if (month === 11) {
    start = new Date(year, 11, 1, 0, 0, 0, 0);
    end = new Date(year + 1, 1, 1, 0, 0, 0, 0);
  } else {
    start = new Date(year, 11, 1, 0, 0, 0, 0);
    end = new Date(year + 1, 1, 1, 0, 0, 0, 0);
  }

  const isNewYearSeason = now >= start && now < end;
  const nextBoundary = isNewYearSeason ? end : start;

  return { isNewYearSeason, nextBoundary };
};

const getAdaptiveInterval = (msUntilBoundary: number): number => {
  if (msUntilBoundary <= 0) return MINUTE;
  if (msUntilBoundary <= 10 * MINUTE) return MINUTE;
  if (msUntilBoundary <= HOUR) return 5 * MINUTE;
  if (msUntilBoundary <= 6 * HOUR) return 10 * MINUTE;
  if (msUntilBoundary <= DAY) return 10 * MINUTE;
  if (msUntilBoundary <= 7 * DAY) return HOUR;
  if (msUntilBoundary <= 31 * DAY) return 6 * HOUR;
  return DAY;
};

let timerId: ReturnType<typeof setTimeout> | null = null;
let isRunning = false;

const scheduleNextCheck = (set: (state: Partial<NewYearSeasonState>) => void) => {
  const now = new Date();
  const { isNewYearSeason, nextBoundary } = getSeasonStatus(now);
  const msUntilBoundary = nextBoundary.getTime() - now.getTime();
  const interval = Math.min(msUntilBoundary, getAdaptiveInterval(msUntilBoundary));
  const safeInterval = interval > 0 ? interval : MINUTE;

  if (timerId) clearTimeout(timerId);

  set({ isNewYearSeason, nextCheckAt: Date.now() + safeInterval });

  timerId = setTimeout(() => {
    if (!isRunning) return;
    scheduleNextCheck(set);
  }, safeInterval);
};

export const useNewYearSeasonStore = create<NewYearSeasonState>(set => ({
  isNewYearSeason: getSeasonStatus().isNewYearSeason,
  nextCheckAt: null,
  start: () => {
    if (isRunning) return;
    if (typeof window === 'undefined') return;
    isRunning = true;
    scheduleNextCheck(set);
  },
  stop: () => {
    isRunning = false;
    if (timerId) {
      clearTimeout(timerId);
      timerId = null;
    }
  },
  refresh: () => {
    if (!isRunning) return;
    scheduleNextCheck(set);
  },
}));
